import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import {
  cancelExcelJob,
  getExcelFile,
  getExcelJob,
  getLatestExcelJob,
  GstJob,
  GstJobUnit,
  startExcelJob,
} from "api/services/gstr/returnReports";
import { blobErrorMessage, fileNameFromHeaders, saveBlob } from "utils/blobFile";
import { snack } from "components/toast";

/** Returns offered as Excel (same files as the GST Download page), in the order of the buttons. */
export const EXCEL_RETURNS: { key: string; label: string }[] = [
  { key: "GSTR1", label: "GSTR-1/IFF" },
  { key: "GSTR1A", label: "GSTR-1A" },
  { key: "GSTR2A", label: "GSTR-2A" },
  { key: "GSTR2B", label: "GSTR-2B" },
  { key: "GSTR3B", label: "GSTR-3B" },
  { key: "GSTR9", label: "GSTR-9" },
  { key: "GSTR9C", label: "GSTR-9C" },
];

const RUNNING = new Set<GstJob["state"]>(["queued", "running"]);
const POLL_MS = 3000;
const JOB_CHIP: Record<GstJob["state"], { label: string; color: string; bg: string }> = {
  queued: { label: "Queued", color: "#1D4ED8", bg: "#DBEAFE" },
  running: { label: "Fetching", color: "#1D4ED8", bg: "#DBEAFE" },
  done: { label: "Completed", color: "#047857", bg: "#D1FAE5" },
  partial: { label: "Completed with failures", color: "#B45309", bg: "#FEF3C7" },
  failed: { label: "Failed", color: "#B91C1C", bg: "#FEE2E2" },
  cancelled: { label: "Cancelled", color: "#475569", bg: "#F1F5F9" },
  interrupted: { label: "Interrupted", color: "#B45309", bg: "#FEF3C7" },
};
const UNIT_CHIP: Record<GstJobUnit["state"], { label: string; color: string; bg: string }> = {
  pending: { label: "Waiting", color: "#475569", bg: "#F1F5F9" },
  running: { label: "Fetching", color: "#1D4ED8", bg: "#DBEAFE" },
  done: { label: "Fetched", color: "#047857", bg: "#D1FAE5" },
  failed: { label: "Failed", color: "#B91C1C", bg: "#FEE2E2" },
  skipped: { label: "Skipped", color: "#475569", bg: "#F1F5F9" },
};

// GSTR-9 and 9C are fetched together as one annual unit
const unitsOf = (job: GstJob, ret: string): GstJobUnit[] =>
  job.units.filter((u) => u.ret === (ret === "GSTR9" || ret === "GSTR9C" ? "ANNUAL" : ret));

interface Props {
  open: boolean;
  onClose: () => void;
  credentialId: number; // gstr_credentials.id of the client's GST login
  clientName: string;
  gstin?: string;
  ret: string; // one of EXCEL_RETURNS
  fy: string;
}

/** One return as Excel (same file as the GST Download page), fetched with the client's saved GST login. */
const ReturnExcelDialog = ({ open, onClose, credentialId, clientName, gstin, ret, fy }: Props) => {
  const label = EXCEL_RETURNS.find((r) => r.key === ret)?.label ?? ret;
  const [job, setJob] = useState<GstJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // this login's latest download of the return for the FY (a running one is resumed)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setJob(null);
    getLatestExcelJob(credentialId, fy, ret)
      .then((latest) => {
        if (!cancelled) setJob(latest);
      })
      .catch(async (err) => {
        if (!cancelled) snack.error(await blobErrorMessage(err, `Could not load the ${label} download.`));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [credentialId, fy, ret, label]);

  const running = !!job && RUNNING.has(job.state);
  const jobId = job?.job_id;
  useEffect(() => {
    if (!running || !jobId) return;
    const t = window.setTimeout(() => {
      getExcelJob(credentialId, jobId)
        .then(setJob)
        .catch(() => setJob((j) => (j ? { ...j } : j))); // a missed poll: try again on the next tick
    }, POLL_MS);
    return () => window.clearTimeout(t);
  }, [running, jobId, job, credentialId]);

  const start = async () => {
    setStarting(true);
    try {
      setJob(await startExcelJob(credentialId, { fy, returns: [ret] }));
    } catch (err) {
      snack.error(await blobErrorMessage(err, "Could not start the GST download."));
    } finally {
      setStarting(false);
    }
  };

  const cancel = async () => {
    if (!jobId) return;
    try {
      await cancelExcelJob(credentialId, jobId);
    } catch (err) {
      snack.error(await blobErrorMessage(err, "Could not cancel."));
    }
  };

  const download = async () => {
    if (!jobId) return;
    setDownloading(true);
    try {
      const res: any = await getExcelFile(credentialId, jobId, ret);
      saveBlob(res.data, fileNameFromHeaders(res.headers, `${ret}_FY${fy}.xlsx`));
    } catch (err) {
      snack.error(await blobErrorMessage(err, "Could not build the Excel."));
    } finally {
      setDownloading(false);
    }
  };

  const units = job ? unitsOf(job, ret) : [];
  const finished = units.filter((u) => u.state !== "pending" && u.state !== "running").length;
  const fetched = units.filter((u) => u.state === "done").length;
  const failed = units.filter((u) => u.state === "failed").length;
  const pct = units.length ? Math.round((finished / units.length) * 100) : 0;
  const chip = job ? JOB_CHIP[job.state] ?? JOB_CHIP.queued : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="primary.main">
            {clientName}
            {gstin ? ` · ${gstin}` : ""}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label} Excel — FY {fy}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={2}>
              <Button
                variant="contained"
                disableElevation
                startIcon={starting ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadOutlinedIcon />}
                disabled={running || starting}
                onClick={start}
                sx={{ textTransform: "none" }}
              >
                {starting ? "Starting…" : job ? `Fetch ${label} again` : `Fetch ${label} from GST portal`}
              </Button>
              <Button
                variant="outlined"
                startIcon={downloading ? <CircularProgress size={16} /> : <DownloadOutlinedIcon />}
                disabled={!job || running || !fetched || downloading}
                onClick={download}
                sx={{ textTransform: "none" }}
              >
                Download Excel
              </Button>
              {chip && <Chip size="small" label={chip.label} sx={{ fontWeight: 600, color: chip.color, bgcolor: chip.bg }} />}
              <Box flexGrow={1} />
              {running && (
                <Button size="small" color="error" startIcon={<StopCircleOutlinedIcon />} onClick={cancel} sx={{ textTransform: "none" }}>
                  Cancel
                </Button>
              )}
            </Box>

            {!job && (
              <Typography variant="body2" color="text.secondary">
                Nothing downloaded yet for FY {fy}. Click &quot;Fetch {label} from GST portal&quot; — it uses the client&apos;s saved GST
                login. Avoid logging in to this GST account elsewhere while it runs (the portal allows one session per login).
              </Typography>
            )}

            {job && (
              <>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  {job.stage}
                </Typography>
                <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
                <Typography variant="caption" color="text.secondary">
                  {fetched} of {units.length} fetched{failed ? ` · ${failed} failed` : ""}
                  {running ? " — you can close this window, the download continues." : ""}
                </Typography>
                {job.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {job.error}
                  </Alert>
                )}
                <Table size="small" sx={{ mt: 1.5 }}>
                  <TableHead>
                    <TableRow>
                      {["Month", "Status", "Details"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 600, bgcolor: "#F8FAFC" }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {units.map((u) => {
                      const c = UNIT_CHIP[u.state] ?? UNIT_CHIP.pending;
                      return (
                        <TableRow key={`${u.ret}_${u.period}`} hover>
                          <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 500 }}>
                            {u.label}
                          </TableCell>
                          <TableCell>
                            {u.state === "running" ? (
                              <CircularProgress size={14} />
                            ) : (
                              <Chip size="small" label={u.cached ? "Earlier download" : c.label} sx={{ fontWeight: 600, color: c.color, bgcolor: c.bg }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {u.error || u.summary || ""}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {!running && failed > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Failed months: click &quot;Fetch {label} again&quot; — months already downloaded are reused, only the rest is fetched.
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReturnExcelDialog;
